import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

const REQUEST_ID_HEADER = 'x-request-id';

export interface Kcc20UserDraft {
  draftId: string;
  type: 'ask' | 'bid' | 'fillAsk' | 'fillBid' | 'cancel' | string;
  tokenId?: string;
  orderId?: string;
  payload: Record<string, unknown>;
  updatedAt: string;
}

export interface Kcc20NotificationSettings {
  fills: boolean;
  indexedTxs: boolean;
  watchedMarkets: boolean;
  stalePendingTxs: boolean;
}

export interface Kcc20UserPreferences {
  defaultQuoteAmount?: string;
  defaultCandleIntervalMs?: string;
  theme?: 'light' | 'dark' | 'system' | string;
  extra?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface Kcc20UserState {
  owner: string;
  watchlist: string[];
  savedMarkets: string[];
  preferences: Kcc20UserPreferences;
  drafts: Kcc20UserDraft[];
  notifications: Kcc20NotificationSettings;
  createdAt?: string;
  updatedAt?: string;
}

export interface Kcc20UpdatePreferences {
  defaultQuoteAmount?: string;
  defaultCandleIntervalMs?: string;
  theme?: 'light' | 'dark' | 'system';
  extra?: Record<string, unknown>;
}

export interface Kcc20UpdateNotifications {
  fills?: boolean;
  indexedTxs?: boolean;
  watchedMarkets?: boolean;
  stalePendingTxs?: boolean;
}

export interface Kcc20UpdateDraft {
  type:
    | 'ask'
    | 'bid'
    | 'fillAsk'
    | 'fillBid'
    | 'cancel'
    | 'deployToken'
    | 'feeTicketCreate'
    | 'feeTicketBurn'
    | 'mintToken'
    | 'wrap'
    | 'unwrap';
  tokenId?: string;
  orderId?: string;
  payload: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class Kcc20UserStateApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;

  getMyState(requestId?: string): Observable<Kcc20UserState> {
    return this.http.get<Kcc20UserState>(
      `${this.baseUrl}/users/me/state`,
      this.options(requestId),
    );
  }

  addMyWatchlist(
    tokenId: string,
    requestId?: string,
  ): Observable<Kcc20UserState> {
    return this.http.put<Kcc20UserState>(
      `${this.baseUrl}/users/me/watchlist/${tokenId}`,
      {},
      this.options(requestId),
    );
  }

  removeMyWatchlist(
    tokenId: string,
    requestId?: string,
  ): Observable<Kcc20UserState> {
    return this.http.delete<Kcc20UserState>(
      `${this.baseUrl}/users/me/watchlist/${tokenId}`,
      this.options(requestId),
    );
  }

  addMySavedMarket(
    tokenId: string,
    requestId?: string,
  ): Observable<Kcc20UserState> {
    return this.http.put<Kcc20UserState>(
      `${this.baseUrl}/users/me/saved-markets/${tokenId}`,
      {},
      this.options(requestId),
    );
  }

  removeMySavedMarket(
    tokenId: string,
    requestId?: string,
  ): Observable<Kcc20UserState> {
    return this.http.delete<Kcc20UserState>(
      `${this.baseUrl}/users/me/saved-markets/${tokenId}`,
      this.options(requestId),
    );
  }

  patchMyPreferences(
    dto: Kcc20UpdatePreferences,
    requestId?: string,
  ): Observable<Kcc20UserState> {
    return this.http.patch<Kcc20UserState>(
      `${this.baseUrl}/users/me/preferences`,
      dto,
      this.options(requestId),
    );
  }

  patchMyNotifications(
    dto: Kcc20UpdateNotifications,
    requestId?: string,
  ): Observable<Kcc20UserState> {
    return this.http.patch<Kcc20UserState>(
      `${this.baseUrl}/users/me/notifications`,
      dto,
      this.options(requestId),
    );
  }

  upsertMyDraft(
    draftId: string,
    dto: Kcc20UpdateDraft,
    requestId?: string,
  ): Observable<Kcc20UserState> {
    return this.http.put<Kcc20UserState>(
      `${this.baseUrl}/users/me/drafts/${draftId}`,
      dto,
      this.options(requestId),
    );
  }

  deleteMyDraft(
    draftId: string,
    requestId?: string,
  ): Observable<Kcc20UserState> {
    return this.http.delete<Kcc20UserState>(
      `${this.baseUrl}/users/me/drafts/${draftId}`,
      this.options(requestId),
    );
  }

  private options(requestId?: string) {
    return {
      withCredentials: true,
      ...headers(requestId),
    };
  }
}

function headers(requestId?: string) {
  return requestId
    ? {
        headers: {
          [REQUEST_ID_HEADER]: requestId,
        },
      }
    : {};
}
